// Deterministic project codenames.
//
// The /infrastructure dashboard renders project nodes by codename instead of
// the real customer name. Same project id ALWAYS maps to the same codename,
// so the map looks stable across reloads and across users.
//
// Two helpers:
//   - projectCodename(id):     async, SubtleCrypto SHA-1, first 4 hex chars.
//                              Used when async is acceptable.
//   - projectCodenameSync(id): sync FNV-1a 32-bit hash, first 4 hex chars.
//                              Used everywhere in v1 to avoid async churn in
//                              React hooks / SSR / useMemo computations.
//
// Both produce a `PRJ-xxxx` string. The two hashes are NOT compatible — a
// given id will get a different 4-hex suffix from each helper. Pick one and
// stick with it per call site. v1 standardises on the sync version.

export const projectCodename = async (projectId) => {
  if (!projectId) return "PRJ-????";
  if (typeof crypto === "undefined" || !crypto.subtle) {
    return projectCodenameSync(projectId);
  }
  const buf = new TextEncoder().encode(projectId);
  const hash = await crypto.subtle.digest("SHA-1", buf);
  const hex = Array.from(new Uint8Array(hash))
    .slice(0, 2)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `PRJ-${hex}`;
};

export const projectCodenameSync = (projectId) => {
  if (!projectId) return "PRJ-????";
  let h = 0x811c9dc5;
  for (let i = 0; i < projectId.length; i += 1) {
    h ^= projectId.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return `PRJ-${h.toString(16).padStart(8, "0").slice(0, 4)}`;
};
